import { memo, useMemo } from 'react'

import { Flex } from '@chakra-ui/react'
import { InspectionArea, InspectionItem, InspectionItemType } from '@editor/interfaces/inspection'
import { InspectionGroup } from '@editor/interfaces/inspectionGroup'
import { ProjectSheetItemsOrder } from '@editor/interfaces/project'
import { Shapes } from '@editor/interfaces/shape'
import { isInspectionGroup } from '@editor/services/InspectionGroup'
import { checkIsModelVisible } from '@editor/services/InspectionSheet'
import { getVolumeEstimationItems } from '@editor/services/VolumeEstimation'
import { RootState } from '@editor/store/app'
import { useSelector } from 'react-redux'
import { useLocation } from 'react-router'

import { GroupLayerRow, InspectionItemLayer, LayerItemRow } from '../interfaces'
import InspectionAreaRowHeader from './InspectionAreaRowHeader'
import Layer from './Layer'
import NoData from './NoData'

interface InspectionAreaRowProps {
  inspectionArea: InspectionArea
  inspectionGroups: InspectionGroup[]
  inspectionItems: InspectionItem[]
  shapes: Shapes

  /**
   * All additional metrics' toggle state, including ones not belonging to this metric.
   */
  allAdditionalMetricsToggle?: boolean[]
}

const InspectionAreaRow = ({
  inspectionArea,
  inspectionGroups,
  inspectionItems,
  shapes,
  allAdditionalMetricsToggle = [],
}: InspectionAreaRowProps) => {
  // URL Params
  const location = useLocation()
  const queries = new URLSearchParams(location.search)
  const inspection_area_id = queries.get('area')

  // Store
  const settings = useSelector((state: RootState) => state.inspectionSheet.settings)

  // Get volume estimation item and all related inspection items (grid, polygon inspection items)
  const volumeSets = useMemo(
    () =>
      getVolumeEstimationItems(inspectionItems).reduce<InspectionItemLayer[][]>((collection, volumeItem) => {
        collection.push([
          volumeItem,
          ...inspectionItems.filter((item) => item.volume_id === volumeItem.inspection_item_id),
        ])
        return collection
      }, []),
    [inspectionItems],
  )

  // Get standalone plane inspection items
  const standalonePlaneItems = inspectionItems
    .filter((item) => item.item_type === InspectionItemType.PolygonArea && !item.volume_id)
    .reduce<InspectionItemLayer[][]>((collection, item) => {
      // Get the shape for the polygon area
      const shape = shapes.polygons.find((s) => s.shape_id === item.shape_ids.polygons[0])
      if (!shape) return collection

      collection.push([
        item,
        ...inspectionItems.filter((i) => i.shape_ids.polygons?.includes(item.shape_ids.polygons[0])),
      ])

      return collection
    }, [])

  // Get custom measurements
  const customMeasurements = inspectionItems.filter(
    (item) => item.item_type === InspectionItemType.LengthWithDistanceTool,
  )

  // Get polylines
  const polylines = inspectionItems
    .filter((item) => item.item_type === InspectionItemType.PolylineLength)
    .map<
      InspectionItemLayer[]
    >((item) => [item, ...inspectionItems.filter((i) => i.polyline_length_id === item.inspection_item_id)])

  // Combine all items into a single array and filter by visibility settings
  const allItems = useMemo(() => {
    let measurements = [customMeasurements]
    if (settings.sheet_order_type_of_items === ProjectSheetItemsOrder.CustomOrder) {
      measurements = customMeasurements.map((item) => [item])
    }

    return (
      [...volumeSets, ...standalonePlaneItems, ...polylines, ...measurements]
        .filter((item) => item[0] && checkIsModelVisible(settings, item[0].item_type))
        // re-sort since the item_type grouping + re-combining have messed it up
        .sort((a, b) => {
          if (settings.sheet_order_type_of_items === ProjectSheetItemsOrder.Name) {
            return a[0].part_name.localeCompare(b[0].part_name)
          }

          if (settings.sheet_order_type_of_items === ProjectSheetItemsOrder.CreatedAt) {
            return (
              new Date(a[0].inspection_item_id!.slice(0, -9)).getTime() -
              new Date(b[0].inspection_item_id!.slice(0, -9)).getTime()
            )
          }

          return (a[0].order_in_inspection_group || 0) - (b[0].order_in_inspection_group || 0)
        })
    )
  }, [polylines, customMeasurements, volumeSets, standalonePlaneItems, settings])

  allItems.map<LayerItemRow>((item) => ({
    inspectionItem: item[0],
    inspectionItems: item,
  }))

  // Sort all items by their group and order.
  const sortedGroups = useMemo(() => {
    if (!settings.sheet_use_inspection_group_order) {
      return []
    }

    // Layers by groups first
    const layers = inspectionGroups
      .sort((a, b) => (a?.order || 0) - (b?.order || 0))
      .map<GroupLayerRow | null>((group) => ({
        key: `inspection-area-${inspectionArea.inspection_area_id}-group-${group.group_id}`,
        group,
        // sort each items in the area according to settings
        items: allItems
          .filter(([item]) => item.inspection_group_id === group.group_id)
          .map<LayerItemRow>((items) => ({
            inspectionItem: items[0],
            inspectionItems: items,
          })),
      }))
      .filter(Boolean) as GroupLayerRow[]

    // Add items without groups
    const itemsWithoutGroup = allItems
      .filter(
        (item) =>
          !item[0].inspection_group_id ||
          !inspectionGroups.some((group) => group.group_id === item[0].inspection_group_id),
      )
      .map<LayerItemRow>((item) => ({
        inspectionItem: item[0],
        inspectionItems: item,
      }))

    return [
      ...layers,
      {
        key: `inspection-area-${inspectionArea.inspection_area_id}-ungrouped`,
        items: itemsWithoutGroup,
        group: undefined,
      },
    ]
  }, [allItems, settings, inspectionArea.inspection_area_id, inspectionGroups])

  return (
    <Flex
      className={['inspection-area-row', inspectionArea.inspection_area_id === inspection_area_id ? 'active' : '']
        .filter(Boolean)
        .join(' ')}
      id={`inspection-area-${inspectionArea.inspection_area_id}`}
    >
      <InspectionAreaRowHeader inspectionArea={inspectionArea} />

      {/* No items at all */}
      {!allItems.length ? (
        <NoData message="このエリアには表示する項目がありません" inspectionArea={inspectionArea} />
      ) : null}

      {/* Sorted by created date */}
      {!sortedGroups.length && allItems.length ? (
        <Flex className="inspection-area-data-rows" flexDirection="column">
          {allItems.map((its, index) => (
            <Layer
              key={`inspection-area-${inspectionArea.inspection_area_id}-item-${its[0].inspection_item_id || index}`}
              inspectionArea={inspectionArea}
              item={its[0]}
              items={its}
              shapes={shapes}
              allAdditionalMetricsToggle={allAdditionalMetricsToggle}
            />
          ))}
        </Flex>
      ) : null}

      {/* Sorted by inspection groups */}
      {sortedGroups.length ? (
        <Flex className="inspection-area-data-rows" flexDirection="column">
          {sortedGroups.map(({ key, group, items }, index) =>
            items.map(({ inspectionItem: it, inspectionItems: its }) => (
              <Layer
                key={`${key}-item-${it.inspection_item_id || index}`}
                inspectionArea={inspectionArea}
                group={isInspectionGroup(group) ? group : undefined}
                item={it}
                items={its}
                shapes={shapes}
                allAdditionalMetricsToggle={allAdditionalMetricsToggle}
              />
            )),
          )}
        </Flex>
      ) : null}
    </Flex>
  )
}

export default memo(InspectionAreaRow)
